import { useEffect, useState } from 'react';
import { ArrowUpRight, Calendar, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { channelLabel, channelsToArray } from '../../lib/channels';
import {
  type CalendarItem,
  calendarItemHref,
  dateFieldLabel,
  formatCalendarDate,
  kindLabel,
  kindPill,
  newsletterStatusCalendarHint,
  openItemLabel,
  statusPill,
} from './calendarShared';

export default function CalendarItemPreviewModal({
  item,
  onClose,
}: {
  item: CalendarItem | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  useEffect(() => {
    if (!item) return;
    setSummaryExpanded(false);
  }, [item?.id]);

  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  if (!item) return null;

  const channelKeys = item.kind === 'content' ? channelsToArray(item.channels) : [];
  const bodyText = typeof item.subtitle === 'string' ? item.subtitle.trim() : '';
  const showSummaryToggle = item.kind === 'transcript' && bodyText.length > 200;

  const openFull = () => {
    navigate(calendarItemHref(item));
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[85] flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden max-h-[min(90vh,40rem)] flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-preview-title"
      >
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${kindPill(item.kind)}`}>
                {kindLabel(item.kind)}
              </span>
              {item.kind === 'content' && item.status ? (
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusPill(item.status)}`}>
                  {item.status}
                </span>
              ) : null}
            </div>
            <h2
              id="calendar-preview-title"
              className="text-base font-semibold text-gray-900 dark:text-white leading-snug"
            >
              {item.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto text-sm text-gray-800 dark:text-gray-200">
          <div className="flex items-start gap-3">
            <Calendar className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">
                {dateFieldLabel(item.kind)}
              </div>
              <div>{formatCalendarDate(item.dateAt)}</div>
            </div>
          </div>

          {item.kind === 'content' && item.status && newsletterStatusCalendarHint(item.status) ? (
            <p className="text-xs text-gray-600 dark:text-gray-400 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
              {newsletterStatusCalendarHint(item.status)}
            </p>
          ) : null}

          {item.kind === 'connection' && bodyText ? (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                Email
              </div>
              <div className="text-gray-700 dark:text-gray-300 break-all">{bodyText}</div>
            </div>
          ) : null}

          {item.kind !== 'connection' && bodyText ? (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                {item.kind === 'transcript' ? 'Summary' : 'Subtitle'}
              </div>
              <p
                className={`text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed ${
                  !summaryExpanded && showSummaryToggle ? 'line-clamp-4' : ''
                }`}
              >
                {bodyText}
              </p>
              {showSummaryToggle ? (
                <button
                  type="button"
                  onClick={() => setSummaryExpanded((v) => !v)}
                  className="mt-2 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                  {summaryExpanded ? 'Show less' : 'Show more'}
                </button>
              ) : null}
            </div>
          ) : null}

          {item.kind === 'content' && !bodyText ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm italic">No subtitle.</p>
          ) : null}

          {item.kind === 'transcript' && !bodyText ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm italic">No summary yet.</p>
          ) : null}

          {channelKeys.length > 0 ? (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Channels
              </div>
              <div className="flex flex-wrap gap-1.5">
                {channelKeys.map((chKey) => (
                  <span
                    key={chKey}
                    className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200"
                  >
                    {channelLabel(chKey)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 shrink-0 bg-gray-50/80 dark:bg-gray-900/40">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800"
          >
            Close
          </button>
          <button
            type="button"
            onClick={openFull}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium"
          >
            {openItemLabel(item.kind)}
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
